import {Box, Radio, Tab, Tabs, Typography} from '@mui/material';
import {t} from 'i18next';
import {useRef, useState, useEffect} from 'react';
import './ViewTemplates.scss';
import ArrDataGrid from '../../../common/arr-data-grid/ArrDataGrid';
import ArrIconButton from '../../../common/arr-icon-button/ArrIconButton';
import {ReactComponent as MoreIcon} from '../../../../assets/images/icon-more.svg';
import {ReactComponent as CloseToast} from '../../../../../src/assets/images/close-toast.svg';
import {ReactComponent as InfoIcon} from '../../../../assets/images/info-icon.svg';
import {PAGE_SIZE, TOAST_DURATION} from '../../../../shared/constants';
import {GridRenderCellParams, GridSortModel} from '@mui/x-data-grid';
import {
  manageTemplateDetailsMoreMenu,
  manageTemplatesColumns,
  getViewTemplateTabs,
  isProductionSetFromTemplateSetType,
} from '../manageTemplatesUtils';
import {
  useDeleteManageTemplate,
  useGetManageTemplates,
  useSecurityGroupDefaultSaveTemplate,
} from '../../../../hooks/productions/useProductions';
import {GetManageTemplatesRequestParam} from '../../../../api/types/production.type';
import ArrMenu, {
  ArrMenuHandle,
  CustomMenuItem,
} from '../../../common/arr-menu/ArrMenu';
import ArrModal from '../../../common/arr-modal/ArrModal';
import ArrToast from '../../../common/arr-toast/ArrToast';
import ArrTooltipWrapper from '../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {ReactComponent as RadioEnableIcon} from '../../../../assets/images/radio-enable-icon.svg';
import {ReactComponent as RadioActiveIcon} from '../../../../assets/images/radio-active-icon.svg';
import {formatDateTime} from '../../../../utils/utils';
import {ArrDrawer} from '../../../common/arr-drawer/ArrDrawer';
import ArrButton from '../../../common/arr-button/ArrButton';
import {ChevronLeft} from '@mui/icons-material';
import PrivGuard from '../../create-new-production/priv-guard/PrivGuard';
import ProductionComponents from '../../create-new-production/production-components/ProductionComponents';
import NumberingSorting from '../../create-new-production/numbering-sorting/NumberingSorting';
import {FormMode} from '../../create-new-production/CreateNewProductionUtils';

interface ViewTemplatesProps {
  securityGroupId: number;
}

const ViewTemplates: React.FC<ViewTemplatesProps> = ({securityGroupId}) => {
  const [showToast, setShowToast] = useState<boolean>(false);
  const [showSecurityToast, setShowSecurityToast] = useState<boolean>(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: 'templateName',
      sort: 'asc',
    },
  ]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number>(0);
  const [selectedTemplateName, setSelectedTemplateName] = useState<string>('');
  const [selectedTemplateSetType, setSelectedTemplateSetType] =
    useState<string>('');
  const [isDeleteManageTemplateModal, setIsDeleteManageTemplateModal] =
    useState<boolean>(false);
  const [isSecurityGroupTemplateModal, setIsSecurityGroupTemplateModal] =
    useState<boolean>(false);
  const [isViewPanelOpen, setIsViewPanelOpen] = useState<boolean>(false);
  const [viewTemplateTabValue, setViewTemplateTabValue] = useState(1);

  const menuRef = useRef<ArrMenuHandle | null>(null);
  const viewTemplateContainerRef = useRef<HTMLDivElement | null>(null);
  const infoIconRef = useRef<SVGSVGElement | null>(null);

  // securityGroupId is provided by parent; no need to fetch here

  const {
    data: deleteManageTemplateData,
    mutate: deleteManageTemplate,
    error: deleteManageTemplateError,
  } = useDeleteManageTemplate();

  const {
    data: securityGroupDefultSaveTemplateData,
    mutate: securityGroupDefultSaveTemplate,
    error: securityGroupDefultSaveTemplateError,
  } = useSecurityGroupDefaultSaveTemplate();

  const {
    data: manageTemplatesListData,
    isLoading: isManageTemplatesLoading,
    refetch: refetchManageTemplates,
  } = useGetManageTemplates({
    pageNumber: pageNumber,
    pageSize: PAGE_SIZE,
    securityGroupId: securityGroupId,
    isTemplateTypeCustom: true,
    sortColumn: sortModel[0]?.field || 'templateId',
    sortDirection: sortModel[0]?.sort || 'asc',
  } as GetManageTemplatesRequestParam);

  const manageTemplatesWithTempId = manageTemplatesListData?.templatesList.map(
    (u, index) => ({
      ...u,
      tempId: index + 1,
    })
  );

  const isZeroState =
    !isManageTemplatesLoading && (manageTemplatesWithTempId?.length ?? 0) === 0;

  const OUTER_FOOTER_SELECTOR = [
    '[data-testid="outer-footer"]',
    '[data-testid="manage-templates-footer"]',
    '.outer-footer',
    'footer:not(.MuiDataGrid-footerContainer)',
  ].join(',');

  const FOCUSABLE_SELECTOR = [
    'button:not([disabled])',
    '[href]',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  const focusOuterFooter = () => {
    const outerFooter = document.querySelector<HTMLElement>(
      OUTER_FOOTER_SELECTOR
    );
    if (!outerFooter) {
      return;
    }
    const focusableChild =
      outerFooter.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
    if (focusableChild) {
      focusableChild.focus();
      return;
    }
    if (!outerFooter.hasAttribute('tabindex')) {
      outerFooter.setAttribute('tabindex', '-1');
    }
    outerFooter.focus();
  };

  const handleInfoIconKeyDown = (event: React.KeyboardEvent<SVGSVGElement>) => {
    if (event.key !== 'Tab' || event.shiftKey || !isZeroState) {
      return;
    }
    event.preventDefault();
    window.requestAnimationFrame(() => {
      focusOuterFooter();
    });
  };

  const updatedColumns = [
    ...manageTemplatesColumns.map((col) => {
      if (col.field === 'isSecurityGroupTemplate') {
        return {
          ...col,
          renderCell: (params: GridRenderCellParams) => {
            const {templateId} = params.row;
            const isSelected = params.value === true;
            return (
              <Radio
                slotProps={{
                  input: {
                    'aria-label': `Set ${params.row.templateName} as security group template`,
                    'data-testid': `radio-${col.field}`,
                    tabIndex: 0,
                  } as React.InputHTMLAttributes<HTMLInputElement>,
                }}
                className="radio-group"
                checked={isSelected}
                onChange={() => {
                  setSelectedTemplateId(templateId);
                  setIsSecurityGroupTemplateModal(true);
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    setSelectedTemplateId(templateId);
                    setIsSecurityGroupTemplateModal(true);
                  }
                }}
                icon={<RadioEnableIcon />}
                checkedIcon={<RadioActiveIcon />}
                data-testid={`sec-group-template-${params.id}`}
              />
            );
          },
        };
      }
      if (col.field === 'dateCreated') {
        return {
          ...col,
          renderCell: (params: GridRenderCellParams) => {
            const formattedDateCreated = formatDateTime(params.value);
            return (
              <span data-testid={`date-created-${params.id}`}>
                {formattedDateCreated === '--'
                  ? ''
                  : formattedDateCreated || ' '}
              </span>
            );
          },
        };
      }
      if (col.field === 'lastModifiedDate') {
        return {
          ...col,
          renderCell: (params: GridRenderCellParams) => {
            const formattedLastModified = formatDateTime(params.value);
            return (
              <span data-testid={`datemodified-${params.id}`}>
                {formattedLastModified || ' '}
              </span>
            );
          },
        };
      }

      return col;
    }),
    {
      field: 'action',
      headerName: '',
      width: 50,
      sortable: false,
      filterable: false,
      headerClassName: 'typo-text label2_size more-option-header',
      renderCell: (params: GridRenderCellParams) => {
        const {templateId, templateName, setType} = params.row;
        return (
          <ArrIconButton
            onClick={(e) =>
              handleMoreIconClick(e, templateId, templateName, setType)
            }
            icon={<MoreIcon />}
            ariaLabel="More options"
            shape="none"
            className="templates-grid-edit-icon"
            testId={`view-templates-action-button-${params.id}`}
          />
        );
      },
    },
  ];

  const onPageChangeHandler = (value: number) => {
    setPageNumber(value);
  };
  const handleSortModelChange = (model: GridSortModel) => {
    setSortModel(model);
  };

  const handleMoreIconClick = (
    e: React.MouseEvent<HTMLElement>,
    templateId: number,
    templateName: string,
    setType: string
  ) => {
    menuRef.current?.open(e.currentTarget as HTMLElement);
    setSelectedTemplateId(templateId);
    setSelectedTemplateName(templateName);
    setSelectedTemplateSetType(setType ?? '');
  };

  const onMenuItemClick = (item: CustomMenuItem) => {
    if (item.id === 'view') {
      setIsViewPanelOpen(true);
      setIsDeleteManageTemplateModal(false);
    } else if (item.id === 'delete') {
      setIsDeleteManageTemplateModal(true);
      setIsViewPanelOpen(false);
    }
  };

  const ConfirmDeleteManageTemplate = () => {
    deleteManageTemplate(selectedTemplateId, {
      onSuccess: () => {
        if (pageNumber !== 1) {
          setPageNumber(1);
        } else {
          refetchManageTemplates();
        }
        setShowToast(true);
      },
      onError: () => {
        setShowToast(true);
      },
    });

    setIsDeleteManageTemplateModal(false);
  };

  const ConfirmSecurityGroupTemplate = () => {
    const defaultSavePayload = {
      templateId: selectedTemplateId,
      isTemplateTypeCustom: true,
      securityGroupId: securityGroupId || 0,
    };
    securityGroupDefultSaveTemplate(defaultSavePayload, {
      onSuccess: () => {
        if (pageNumber !== 1) {
          setPageNumber(1);
        } else {
          refetchManageTemplates();
        }
        setShowSecurityToast(true);
      },
      onError: () => {
        setShowSecurityToast(true);
      },
    });

    setIsSecurityGroupTemplateModal(false);
  };

  const handleViewTemplateTabChange = (
    event: React.SyntheticEvent,
    newValue: number
  ) => {
    setViewTemplateTabValue(newValue);
  };

  const viewTemplateTabs = getViewTemplateTabs();
  const isViewTemplateProductionSet = isProductionSetFromTemplateSetType(
    selectedTemplateSetType
  );

  const updateProductionComponentsAccessibility = () => {
    const container = viewTemplateContainerRef.current;
    if (!container || viewTemplateTabValue !== 1) {
      return;
    }

    const tabContent = container.querySelector<HTMLElement>(
      '.view-template-panel-tab-content'
    );
    if (!tabContent) {
      return;
    }

    const accordionLabels = ['DAT', 'Native', 'TIFF / PDF', 'Text'];

    accordionLabels.forEach((label) => {
      const labelMatcher = (text: string) => {
        const normalizedText = text.replace(/\s+/g, ' ').trim().toLowerCase();
        return normalizedText.includes(label.toLowerCase());
      };

      const matchingLabel = Array.from(
        tabContent.querySelectorAll<HTMLElement>('*')
      ).find((element) => labelMatcher(element.textContent || ''));

      const accordionRow =
        matchingLabel?.closest<HTMLElement>(
          '[role="button"], [role="row"], .MuiAccordionSummary-root, .MuiAccordion-root, .accordion-row'
        ) || matchingLabel?.parentElement;

      if (!accordionRow) {
        return;
      }

      const checkbox = accordionRow.querySelector<HTMLElement>(
        'input[type="checkbox"], [role="checkbox"]'
      );
      if (checkbox) {
        setTabIndexIfNeeded(checkbox, '0');
      }

      const expandToggle =
        accordionRow.querySelector<HTMLElement>(
          'button[aria-expanded], [role="button"][aria-expanded], .MuiAccordionSummary-expandIconWrapper, button'
        ) ||
        accordionRow.querySelector<HTMLElement>('svg, [class*="expand" i]');

      if (expandToggle) {
        setTabIndexIfNeeded(expandToggle, '0');
        if (!expandToggle.getAttribute('aria-label')) {
          expandToggle.setAttribute('aria-label', `${label} expand toggle`);
        }
        if (!expandToggle.hasAttribute('aria-expanded')) {
          expandToggle.setAttribute('aria-expanded', 'false');
        }
      }
    });

    const optionalInstructionsToggle = Array.from(
      tabContent.querySelectorAll<HTMLElement>(
        'button, a, [role="button"], [tabindex]'
      )
    ).find((element) =>
      /show\s+optional\s+instructions/i.test(element.textContent || '')
    );

    if (optionalInstructionsToggle) {
      setTabIndexIfNeeded(optionalInstructionsToggle, '0');
      optionalInstructionsToggle.setAttribute('aria-expanded', 'false');
    }
  };

  useEffect(() => {
    if (!isViewPanelOpen || viewTemplateTabValue !== 1) {
      return;
    }

    updateProductionComponentsAccessibility();

    const container = viewTemplateContainerRef.current;
    const tabContent = container?.querySelector<HTMLElement>(
      '.view-template-panel-tab-content'
    );
    if (!tabContent) {
      return;
    }

    const observer = new MutationObserver(() => {
      updateProductionComponentsAccessibility();
    });

    observer.observe(tabContent, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-expanded', 'tabindex'],
    });

    return () => {
      observer.disconnect();
    };
  }, [isViewPanelOpen, viewTemplateTabValue, selectedTemplateId]);

  const viewTemplateContent = (
    <Box>
      <Box className="view-template-panel-container">
        <Box className="view-template-panel-header">
          <Box className="view-template-panel-title">
            <Typography
              className=" typo-text headline3_size title"
              data-testid={`view-templates-selected-template-name`}
            >
              {selectedTemplateName}
            </Typography>
          </Box>
          <Box className="view-template-action-buttons">
            {viewTemplateTabValue > 0 && (
              <ArrButton
                dataTestId="view-template-back-button"
                variant="secondary"
                size="medium"
                startIcon={<ChevronLeft />}
                onClick={() =>
                  setViewTemplateTabValue(viewTemplateTabValue - 1)
                }
              >
                {t('common.back')}
              </ArrButton>
            )}
            {viewTemplateTabValue < viewTemplateTabs.length - 1 && (
              <ArrButton
                dataTestId="view-template-next-button"
                variant="primary"
                size="medium"
                onClick={() =>
                  setViewTemplateTabValue(viewTemplateTabValue + 1)
                }
              >
                {t('common.next')}
              </ArrButton>
            )}
          </Box>
        </Box>
        <Box className="view-template-panel-content">
          <Tabs
            value={viewTemplateTabValue}
            onChange={handleViewTemplateTabChange}
            aria-label="View Templates"
            className="view-template-panel-tabs"
            data-testid="view-template-panel-tabs"
          >
            {viewTemplateTabs.map((tab, index) => (
              <Tab
                key={tab.label}
                label={tab.label}
                id={`view-template-panel-tab-${index}`}
                aria-controls={`view-template-panel-tabpanel-${index}`}
                aria-selected={viewTemplateTabValue === index}
                className="typo-text subhead4_size view-template-panel-tab-item"
                data-testid={`view-template-panel-tab-item-${index}`}
              />
            ))}
          </Tabs>
          <Box
            className="view-template-panel-tab-content"
            id={`view-template-panel-tabpanel-${viewTemplateTabValue}`}
            role="tabpanel"
            aria-labelledby={`view-template-panel-tab-${viewTemplateTabValue}`}
          >
            {viewTemplateTabValue === 0 && (
              <PrivGuard
                isProductionSet={isViewTemplateProductionSet}
                productionId={0}
                productionName={''}
                mode={FormMode.VIEW}
                isTemplate={true}
                templateId={selectedTemplateId}
              />
            )}
            {viewTemplateTabValue === 1 && (
              <ProductionComponents
                key={`template-pc-${selectedTemplateId}`}
                isProductionSet={isViewTemplateProductionSet}
                productionId={0}
                exportProductionId={0}
                mode={FormMode.VIEW}
                isTemplate={true}
                templateId={selectedTemplateId}
              />
            )}
            {viewTemplateTabValue === 2 && (
              <NumberingSorting
                isProductionSet={isViewTemplateProductionSet}
                productionId={0}
                productionName={''}
                mode={FormMode.VIEW}
                isTemplate={true}
                templateId={selectedTemplateId}
              />
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );

  const ACTION_MENU_BUTTON_SELECTOR = [
    'button[data-testid^="view-templates-action-button-"]',
    '[data-testid^="view-templates-action-button-"] button',
    '[data-testid^="view-templates-action-button-"][role="button"]',
  ].join(',');

  const PAGINATION_CONTROL_SELECTOR = [
    '.MuiTablePagination-root button',
    '.MuiPagination-root button',
    '.MuiDataGrid-footerContainer button',
    '.MuiDataGrid-pagination button',
    '[aria-label*="page" i]',
    '[aria-label*="next" i]',
    '[aria-label*="previous" i]',
    '[aria-label*="first" i]',
    '[aria-label*="last" i]',
  ].join(',');

  const setTabIndexIfNeeded = (element: HTMLElement, value: string) => {
    if (element.getAttribute('tabindex') !== value) {
      element.setAttribute('tabindex', value);
    }
  };

  const getFocusableGridControl = (cell: HTMLElement): HTMLElement | null => {
    const radio = cell.querySelector<HTMLElement>('input[type="radio"]');
    if (radio) {
      return radio;
    }
    return cell.querySelector<HTMLElement>(ACTION_MENU_BUTTON_SELECTOR);
  };

  const updateGridTabOrder = () => {
    const container = viewTemplateContainerRef.current;
    if (!container) {
      return;
    }
    const grid = container.querySelector<HTMLElement>('.view-templates-grid');
    if (!grid) {
      return;
    }
    setTabIndexIfNeeded(grid, '-1');
    if (isZeroState) {
      grid
        .querySelectorAll<HTMLElement>(
          [
            '[tabindex]',
            'button',
            'input',
            'select',
            'textarea',
            'a[href]',
            '[role="grid"]',
            '[role="row"]',
            '[role="gridcell"]',
            '[role="columnheader"]',
            '.MuiDataGrid-root',
            '.MuiDataGrid-main',
            '.MuiDataGrid-virtualScroller',
            '.MuiDataGrid-overlayWrapper',
            '.MuiDataGrid-overlay',
            '.MuiDataGrid-footerContainer',
            '.MuiTablePagination-root',
            '.MuiPagination-root',
          ].join(',')
        )
        .forEach((element) => {
          setTabIndexIfNeeded(element, '-1');
        });
      return;
    }
    grid
      .querySelectorAll<HTMLElement>(
        [
          '[role="grid"]',
          '[role="row"]',
          '[role="gridcell"]',
          '[role="columnheader"]',
          '.MuiDataGrid-cell',
          '.MuiDataGrid-columnHeader',
        ].join(',')
      )
      .forEach((element) => {
        setTabIndexIfNeeded(element, '-1');
      });
    grid
      .querySelectorAll<HTMLElement>(
        ['button', 'input', 'select', 'textarea', 'a[href]', '[tabindex]'].join(
          ','
        )
      )
      .forEach((element) => {
        setTabIndexIfNeeded(element, '-1');
      });
    grid
      .querySelectorAll<HTMLElement>('input[type="radio"]')
      .forEach((radio) => {
        setTabIndexIfNeeded(radio, '0');
      });
    grid
      .querySelectorAll<HTMLElement>(ACTION_MENU_BUTTON_SELECTOR)
      .forEach((button) => {
        setTabIndexIfNeeded(button, '0');
        if (!button.getAttribute('aria-label')) {
          button.setAttribute('aria-label', 'More options');
        }
      });
    grid
      .querySelectorAll<HTMLElement>(PAGINATION_CONTROL_SELECTOR)
      .forEach((control) => {
        const isDisabled =
          control.hasAttribute('disabled') ||
          control.getAttribute('aria-disabled') === 'true';
        if (isDisabled) {
          setTabIndexIfNeeded(control, '-1');
          return;
        }
        setTabIndexIfNeeded(control, '0');
        const existingLabel = control.getAttribute('aria-label');
        if (existingLabel) {
          return;
        }
        const text = control.textContent?.trim();
        if (text) {
          control.setAttribute('aria-label', `Go to page ${text}`);
        }
      });
  };

  useEffect(() => {
    const container = viewTemplateContainerRef.current;
    if (!container) {
      return;
    }
    const grid = container.querySelector<HTMLElement>('.view-templates-grid');
    if (!grid) {
      return;
    }
    const paginationRegion =
      grid.querySelector<HTMLElement>('.MuiTablePagination-root') ||
      grid.querySelector<HTMLElement>('.MuiPagination-root') ||
      grid.querySelector<HTMLElement>('.MuiDataGrid-footerContainer');
    if (paginationRegion) {
      paginationRegion.setAttribute('aria-label', 'Table pagination');
    }
    let animationFrameId: number | null = null;

    const scheduleUpdateGridTabOrder = () => {
      if (animationFrameId !== null) {
        return;
      }
      animationFrameId = window.requestAnimationFrame(() => {
        updateGridTabOrder();
        animationFrameId = null;
      });
    };

    const moveFocusFromCellToControl = (target: EventTarget | null) => {
      if (!(target instanceof HTMLElement)) {
        return;
      }
      const cell = target.closest<HTMLElement>(
        '[role="gridcell"], .MuiDataGrid-cell'
      );
      if (!cell) {
        return;
      }
      const control = getFocusableGridControl(cell);
      if (!control) {
        return;
      }
      if (document.activeElement === cell) {
        window.requestAnimationFrame(() => {
          control.focus();
        });
      }
    };

    const handleFocusIn = (event: FocusEvent) => {
      if (isZeroState) {
        event.stopPropagation();

        window.requestAnimationFrame(() => {
          focusOuterFooter();
        });

        return;
      }
      moveFocusFromCellToControl(event.target);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      if (!(event.target instanceof HTMLElement)) {
        return;
      }
      const isRadioTarget = event.target.matches('input[type="radio"]');
      const isActionButtonTarget = Boolean(
        event.target.closest(ACTION_MENU_BUTTON_SELECTOR)
      );
      const isPaginationControlTarget = Boolean(
        event.target.closest(PAGINATION_CONTROL_SELECTOR)
      );
      if (isRadioTarget || isActionButtonTarget || isPaginationControlTarget) {
        return;
      }
      const cell = event.target.closest<HTMLElement>(
        '[role="gridcell"], .MuiDataGrid-cell'
      );
      if (!cell) {
        return;
      }
      const control = getFocusableGridControl(cell);
      if (!control) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      control.focus();
      control.click();
    };
    scheduleUpdateGridTabOrder();
    grid.addEventListener('focusin', handleFocusIn, true);
    grid.addEventListener('keydown', handleKeyDown, true);
    const observer = new MutationObserver(() => {
      scheduleUpdateGridTabOrder();
    });
    observer.observe(grid, {
      subtree: true,
      childList: true,
    });
    return () => {
      grid.removeEventListener('focusin', handleFocusIn, true);
      grid.removeEventListener('keydown', handleKeyDown, true);
      observer.disconnect();

      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId);
      }
    };
  }, [
    manageTemplatesListData,
    isManageTemplatesLoading,
    pageNumber,
    sortModel,
  ]);

  return (
    <Box className="view-template-container" ref={viewTemplateContainerRef}>
      <Box className="view-template-header">
        <Box className="view-template-title">
          <Typography
            className="view-template-title-text"
            data-testid={`view-templates-header-title`}
          >
            {t('productionsAndExports.manageTemplates.headerTitle')}
          </Typography>
          <ArrTooltipWrapper
            title={t('productionsAndExports.manageTemplates.headerTitle')}
            trigger={
              <InfoIcon
                ref={infoIconRef}
                fontSize="medium"
                className="info-icon"
                tabIndex={0}
                onKeyDown={handleInfoIconKeyDown}
              />
            }
            closeTooltipOnEscPress
          >
            {t('productionsAndExports.manageTemplates.headerInfo')}
          </ArrTooltipWrapper>
        </Box>
      </Box>
      <ArrDataGrid
        rows={manageTemplatesWithTempId || []}
        columns={updatedColumns}
        rowCount={manageTemplatesListData?.totalRecords ?? 0}
        currentPage={pageNumber}
        itemsPerPage={PAGE_SIZE}
        paginationMode="server"
        onPageChange={onPageChangeHandler}
        entityType={t('common.entries')}
        primaryKey="tempId"
        onSortModelChange={handleSortModelChange}
        sortModel={sortModel}
        className="view-templates-grid"
        loading={isManageTemplatesLoading}
        noRowsOverlay={() => (
          <Box className="no-grid-overlay">
            <Typography
              className="typo-text body3_size"
              data-testid={`view-templates-no-results`}
            >
              {t('productionsAndExports.manageTemplates.noResults')}
            </Typography>
          </Box>
        )}
      />
      <ArrMenu
        id="more-menu"
        ref={menuRef}
        items={manageTemplateDetailsMoreMenu}
        onItemClick={onMenuItemClick}
        direction="left"
      />
      {isDeleteManageTemplateModal && (
        <ArrModal
          showModal={isDeleteManageTemplateModal}
          showClose={false}
          onClose={() => setIsDeleteManageTemplateModal(false)}
          title={t('productionsAndExports.manageTemplates.deletePopup.title')}
          description={t(
            'productionsAndExports.manageTemplates.deletePopup.description'
          )}
          dataTestId="delete-manage-template-modal"
          buttons={[
            {
              id: '1',
              label: t('common.cancel'),
              onClick: () => setIsDeleteManageTemplateModal(false),
              variant: 'secondary',
              dataTestId: 'dmt-cancel-btn',
            },
            {
              id: '2',
              label: t(
                'productionsAndExports.manageTemplates.deletePopup.confirmButton'
              ),
              onClick: ConfirmDeleteManageTemplate,
              variant: 'danger',
              dataTestId: 'dmt-del-btn',
            },
          ]}
        />
      )}
      {showToast && (
        <ArrToast
          id="detele-template-toast"
          title={
            securityGroupDefultSaveTemplateError || deleteManageTemplateError
              ? t('common.error')
              : t('common.success')
          }
          message={
            deleteManageTemplateError?.message ||
            deleteManageTemplateData?.message
          }
          type={deleteManageTemplateError ? 'error' : 'success'}
          duration={TOAST_DURATION}
          onClose={() => setShowToast(false)}
          dismissable={true}
          closeIcon={<CloseToast />}
          position="bottom-right"
        />
      )}
      {showSecurityToast && (
        <ArrToast
          id="detele-template-toast"
          title={
            securityGroupDefultSaveTemplateError
              ? t('common.error')
              : t('common.success')
          }
          message={
            securityGroupDefultSaveTemplateError?.message ||
            securityGroupDefultSaveTemplateData?.message
          }
          type={securityGroupDefultSaveTemplateError ? 'error' : 'success'}
          duration={TOAST_DURATION}
          onClose={() => setShowSecurityToast(false)}
          dismissable={true}
          closeIcon={<CloseToast />}
          position="bottom-right"
        />
      )}
      {isSecurityGroupTemplateModal && (
        <ArrModal
          showModal={isSecurityGroupTemplateModal}
          showClose={false}
          onClose={() => setIsSecurityGroupTemplateModal(false)}
          title={t(
            'productionsAndExports.manageTemplates.securityGroupPopup.title'
          )}
          description={t(
            'productionsAndExports.manageTemplates.securityGroupPopup.description'
          )}
          dataTestId="securtiy-group-template-modal"
          buttons={[
            {
              id: '1',
              label: t('common.cancel'),
              onClick: () => setIsSecurityGroupTemplateModal(false),
              variant: 'secondary',
              dataTestId: 'sgt-cancel-btn',
            },
            {
              id: '2',
              label: t(
                'productionsAndExports.manageTemplates.securityGroupPopup.confirmButton'
              ),
              onClick: ConfirmSecurityGroupTemplate,
              variant: 'primary',
              dataTestId: 'sgt-confirm-btn',
            },
          ]}
        />
      )}
      {isViewPanelOpen && (
        <ArrDrawer
          side="right"
          isOpen={isViewPanelOpen}
          showButton={true}
          size="large"
          onClose={() => {
            setViewTemplateTabValue(0);
            setIsViewPanelOpen(false);
          }}
          className="view-template"
        >
          {viewTemplateContent}
        </ArrDrawer>
      )}
    </Box>
  );
};

export default ViewTemplates;
